import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import TagEditor from "@/components/customers/TagEditor";
import { phoneLengthError, requiredDigits } from "@/lib/phoneFormat";

const EMPTY_FORM = {
  name: "", language: "English", phone: "", alt_phone: "", email: "",
  online_order_no: "", store_location: "", tags: [],
};

export const AddCustomerDialog = ({ open, onOpenChange, countryCodes, onCreated }) => {
  const [newForm, setNewForm] = useState(EMPTY_FORM);

  const submit = async () => {
    if (!newForm.name) return toast.error("Name required");
    const phoneErr = phoneLengthError(newForm.country_code, newForm.phone, countryCodes, "Phone");
    if (phoneErr) return toast.error(phoneErr);
    const altErr = newForm.alt_phone
      ? phoneLengthError(newForm.country_code, newForm.alt_phone, countryCodes, "Alt phone")
      : null;
    if (altErr) return toast.error(altErr);
    try {
      await api.post("/contacts", newForm);
      toast.success("Customer added");
      onOpenChange(false);
      setNewForm({ ...EMPTY_FORM, country_code: "+974" });
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="add-customer-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle className="font-heading">Add customer</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="space-y-1.5"><Label className="text-xs">Name</Label><Input value={newForm.name} onChange={(e) => setNewForm({ ...newForm, name: e.target.value })} data-testid="contact-name-input" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Language</Label><Input value={newForm.language} onChange={(e) => setNewForm({ ...newForm, language: e.target.value })} /></div>
          <div className="space-y-1.5">
            <Label className="text-xs">Country code</Label>
            <Select value={newForm.country_code} onValueChange={(v) => setNewForm({ ...newForm, country_code: v })}>
              <SelectTrigger className="h-9" data-testid="contact-country-code-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {countryCodes.map((c) => (
                  <SelectItem key={c.id} value={c.code}>
                    <span className="font-mono text-xs mr-2">{c.code}</span>{c.country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Phone</Label>
            <Input
              value={newForm.phone}
              onChange={(e) => {
                const cap = requiredDigits(newForm.country_code, countryCodes) || 15;
                setNewForm({ ...newForm, phone: e.target.value.replace(/\D/g, "").slice(0, cap) });
              }}
              placeholder={requiredDigits(newForm.country_code, countryCodes) ? `${requiredDigits(newForm.country_code, countryCodes)} digits` : "Phone"}
              data-testid="contact-phone-input"
            />
            {newForm.phone && phoneLengthError(newForm.country_code, newForm.phone, countryCodes, "Phone") && (
              <p className="text-[11px] text-rose-600" data-testid="contact-phone-error">{phoneLengthError(newForm.country_code, newForm.phone, countryCodes, "Phone")}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Alt phone</Label>
            <Input
              value={newForm.alt_phone}
              onChange={(e) => {
                const cap = requiredDigits(newForm.country_code, countryCodes) || 15;
                setNewForm({ ...newForm, alt_phone: e.target.value.replace(/\D/g, "").slice(0, cap) });
              }}
              data-testid="contact-alt-phone-input"
            />
            {newForm.alt_phone && phoneLengthError(newForm.country_code, newForm.alt_phone, countryCodes, "Alt phone") && (
              <p className="text-[11px] text-rose-600">{phoneLengthError(newForm.country_code, newForm.alt_phone, countryCodes, "Alt phone")}</p>
            )}
          </div>
          <div className="col-span-2 space-y-1.5"><Label className="text-xs">Email</Label><Input value={newForm.email} onChange={(e) => setNewForm({ ...newForm, email: e.target.value })} /></div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-xs">Tags</Label>
            <TagEditor value={newForm.tags} onChange={(v) => setNewForm({ ...newForm, tags: v })} placeholder="Add tag (e.g. vip)…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="contact-submit-btn">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
